import { ECompanyTypeIdConst, ECompanyTypeNameConst } from '@company/companyType/companyType.constants';
import { AppConfigContext } from '@config/AppConfig';
import { useContext, useMemo } from 'react';
import { useAppConfigs, useHasPermission, useIsFromTeam } from '@config/config.hooks';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { EMasterSchedulePermission, ERolePermission } from '@userProfile/permission/permission.constants';
import { useIsLambethOrGammonSite } from '@userProfile/permission/permission.services';
import { EMasterScheduleConfirmationActionType, IMasterScheduleDetail } from './detail/masterScheduleDetail.models';
import { useFetchMasterScheduleDetail } from './detail/masterScheduleDetail.queries';

export const getMasterScheduleDetail = (projectId: number, id: number | undefined, enabled?: boolean) => {
  const { data: masterSchedule, ...rest } = useFetchMasterScheduleDetail({ projectId }, id, enabled);

  const {
    refNo,
    designPackage,
    riskCategory,
    requiredWorkProcedureDrawings,
    procurementDate,
    startConstructionDate,
    removalDate,
    remarks,
    responsibleDesigner,
    responsibleChecker,
    responsibleTwc,
    responsibleTwcm,
    responsibleSrp,
    tags,
  } = masterSchedule || {};

  const designPackageData = {
    refNo,
    designPackage,
    riskCategory,
    requiredWorkProcedureDrawings,
    procurementDate,
    startConstructionDate,
    removalDate,
    remarks,
  };
  const responsiblePersonsAndTeamsData = {
    responsibleDesigner,
    responsibleChecker,
    responsibleTwc,
    responsibleTwcm,
    responsibleSrp,
  };
  return { masterSchedule, designPackageData, responsiblePersonsAndTeamsData, tags, ...rest };
};

export const useMasterScheduleMode: (
  masterSchedule: IMasterScheduleDetail | undefined,
  id: number | undefined,
) => DetailPageAccessMode = (masterSchedule, id) => {
  const appConfigs = useAppConfigs();
  if (!id && !masterSchedule) return 'Create';
  if (masterSchedule?.isCancelled || masterSchedule?.isLocked || masterSchedule?.isDeleted) return 'View';

  const belongToCompanyType = new Set(appConfigs.configs.myCompanies?.map((c) => c.companyTypeId));
  if (belongToCompanyType.size <= 1 && belongToCompanyType.has(ECompanyTypeIdConst.CHECKER)) return 'View';

  return 'Update';
};

export const useBelongToSiteOperationsCompany = (projectId: number) => {
  const appConfigs = useContext(AppConfigContext);
  const isBelongToSiteOperationsCompany = !!appConfigs.configs.myCompanies?.find((s) => {
    return (
      ECompanyTypeNameConst.SITE_OPERATION.toLocaleUpperCase() === s.companyType?.name?.toLocaleUpperCase() &&
      s.projectId === projectId
    );
  });
  return isBelongToSiteOperationsCompany;
};

export const useBelongToCompaniesInMasterSchedule = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const appConfigs = useContext(AppConfigContext);
  const isBelongToDesignerCompany = !!appConfigs.configs.myCompanies?.find((s) => {
    return s.id === masterSchedule?.responsibleDesigner?.id;
  });
  const isBelongToCheckerCompany = !!appConfigs.configs.myCompanies?.find((s) => {
    return s.id === masterSchedule?.responsibleChecker?.id;
  });
  const isBelongToReviewerCompany = !!appConfigs.configs.myCompanies?.find((s) => {
    return s.id === masterSchedule?.responsibleReviewer?.id;
  });
  const isBelongToSiteOperationsCompany = useBelongToSiteOperationsCompany(masterSchedule?.projectId as number);

  return {
    isBelongToCheckerCompany,
    isBelongToDesignerCompany,
    isBelongToReviewerCompany,
    isBelongToSiteOperationsCompany,
  };
};

export const useNominatedInMasterSchedule = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const appConfigs = useContext(AppConfigContext);
  const userProfileId = appConfigs.configs.myProjectUserProfile?.id;

  const isNominatedDesigner = useIsFromTeam(masterSchedule?.responsibleDesignerTeams.map((t) => t.projectTeamId) ?? []);
  const isNominatedChecker = useIsFromTeam(masterSchedule?.responsibleCheckerTeams.map((t) => t.projectTeamId) ?? []);
  const isNominatedReviewer = useIsFromTeam(masterSchedule?.responsibleReviewerTeams.map((t) => t.projectTeamId) ?? []);

  const isSrp = useMemo(() => {
    return !!masterSchedule?.responsibleSrp?.find((srp) => srp.projectUserProfileId === userProfileId);
  }, [JSON.stringify(masterSchedule)]);

  const isTwc = useMemo(() => {
    return !!masterSchedule?.responsibleTwc?.find((twc) => twc.projectUserProfileId === userProfileId);
  }, [JSON.stringify(masterSchedule)]);

  const isTwcm = useMemo(() => {
    return !!masterSchedule?.responsibleTwcm?.find((twcm) => twcm.projectUserProfileId === userProfileId);
  }, [JSON.stringify(masterSchedule)]);

  return { isSrp, isTwc, isTwcm, isNominatedDesigner, isNominatedChecker, isNominatedReviewer };
};

export const useCanUncancelMasterSchedule = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const isTwcRole = useHasPermission(ERolePermission.TEMPORARY_WORK_COORDINATOR);
  const isSiteEngineerRole = useHasPermission(ERolePermission.SITE_ENGINEER);
  const isLambethOrGammonSite = useIsLambethOrGammonSite();

  const isPermitted = useHasPermission(EMasterSchedulePermission.MODIFY_MASTER_SCHEDULE);
  const isPermittedAsDesigner = useHasPermission(
    EMasterSchedulePermission.MODIFY_MASTER_SCHEDULE,
    masterSchedule?.responsibleDesigner?.companyTypeId,
  );
  if (!masterSchedule?.isCancelled) return false;

  if (isTwcRole || isSiteEngineerRole || isLambethOrGammonSite) {
    return isPermitted;
  }
  return isPermittedAsDesigner;
};

export const useCanLockMasterSchedule = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const isTwcRole = useHasPermission(ERolePermission.TEMPORARY_WORK_COORDINATOR);
  const isSiteEngineerRole = useHasPermission(ERolePermission.SITE_ENGINEER);
  const isLambethOrGammonSite = useIsLambethOrGammonSite();

  const isPermitted = useHasPermission(EMasterSchedulePermission.MODIFY_MASTER_SCHEDULE);
  const isPermittedAsDesigner = useHasPermission(
    EMasterSchedulePermission.MODIFY_MASTER_SCHEDULE,
    masterSchedule?.responsibleDesigner?.companyTypeId,
  );

  if (isTwcRole || isSiteEngineerRole || isLambethOrGammonSite) {
    return isPermitted;
  }
  return isPermittedAsDesigner;
};

export const getMasterScheduleConfirmationActionMessage = (
  confirmationType: EMasterScheduleConfirmationActionType | undefined,
) => {
  switch (confirmationType) {
    case EMasterScheduleConfirmationActionType.CANCEL:
      return "Use this if the package isn't required any more. Packages cannot be deleted once any forms are issued. Are you confirm to cancel this T1?";
    case EMasterScheduleConfirmationActionType.UNCANCEL:
      return 'Are you confirm to undo cancel this T1?';
    case EMasterScheduleConfirmationActionType.LOCK:
      return 'Use this if the package is completed. Packages will set to readonly. Are you confirm to lock this T1?';
    case EMasterScheduleConfirmationActionType.UNLOCK:
      return 'Are you confirm to undo locking this T1?';
    default:
      return '';
  }
};

export const useIsDesignerNotApplicable: (masterSchedule: IMasterScheduleDetail | undefined) => boolean = (
  masterSchedule,
) => {
  return !!masterSchedule?.hasNoDesigner;
};
